import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';

interface MessageItemProps {
  message: any;
  authUser: any;
  onDownloadMessageFile: (message: any) => void;
}

export const MessageItem: React.FC<MessageItemProps> = React.memo(({ 
  message, 
  authUser, 
  onDownloadMessageFile 
}) => {
  const isOwnMessage = message.user_id === authUser?.id;
  
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
      gap: 1
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        flexDirection: isOwnMessage ? 'row-reverse' : 'row'
      }}>
        <Chip 
          label={message.user_name}
          size="small"
          color={isOwnMessage ? "primary" : "default"}
          variant="outlined"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {new Date(message.created_at).toLocaleString('tr-TR')}
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2.5,
          maxWidth: '85%',
          background: isOwnMessage ? 
            'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' : 'white',
          color: isOwnMessage ? 'white' : 'text.primary',
          borderRadius: 2,
          boxShadow: isOwnMessage ? 
            '0 4px 12px rgba(25, 118, 210, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
          border: isOwnMessage ? 'none' : '1px solid #e0e0e0',
          position: 'relative',
          '&::before': isOwnMessage ? {
            content: '""',
            position: 'absolute',
            top: 8,
            right: -8,
            width: 0,
            height: 0,
            borderLeft: '8px solid #1976d2',
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent'
          } : {
            content: '""',
            position: 'absolute',
            top: 8,
            left: -8,
            width: 0,
            height: 0,
            borderRight: '8px solid white',
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent'
          }
        }}
      >
        {message.message && (
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              fontSize: '0.875rem'
            }}
          >
            {message.message}
          </Typography>
        )}
        
        {message.file_name && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mt: message.message ? 2 : 0,
              p: 1.5,
              backgroundColor: isOwnMessage ? 
                'rgba(255,255,255,0.1)' : 'grey.50',
              borderRadius: 1,
              border: `1px dashed ${isOwnMessage ? 
                'rgba(255,255,255,0.3)' : 'grey.300'}`
            }}
          >
            <AttachFileIcon 
              fontSize="small" 
              sx={{ 
                color: isOwnMessage ? 'white' : 'primary.main'
              }} 
            />
            <Typography 
              variant="caption" 
              sx={{ 
                flex: 1,
                color: isOwnMessage ? 'white' : 'text.primary',
                fontWeight: 'medium'
              }}
            >
              {message.file_name}
            </Typography>
            <IconButton 
              size="small"
              onClick={() => onDownloadMessageFile(message)}
              sx={{ 
                color: isOwnMessage ? 'white' : 'primary.main',
                backgroundColor: isOwnMessage ? 
                  'rgba(255,255,255,0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: isOwnMessage ? 
                    'rgba(255,255,255,0.3)' : 'grey.100'
                }
              }}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Box>
  );
});